import type { CabinetRoleEditorView, KnownPermission } from "@planerius/contracts/cabinet-auth"
import { PERMISSION_GROUPS, SYSTEM_ROLES } from "@planerius/contracts/cabinet-roles"

/**
 * РЕДАКТОР РОЛЕЙ (ТЗ 04, Б11; макет П2, 4-Б11) — правила без вёрстки.
 *
 * Галочка раздела управляет группой прав целиком. Часть группы — третье состояние,
 * «частично»: системные роли собраны из отдельных прав, и администратору, например,
 * выдано «вести графики», но не «заводить сотрудников». Показать такую группу пустой
 * или полной значило бы соврать, а нажатие по ней — молча выдать или снять лишнее.
 */

export interface RoleForm {
  readonly name: string
  readonly permissions: ReadonlySet<string>
  readonly restrictToOwnRecords: boolean
  readonly requireMfa: boolean
}

export function formOf(role: CabinetRoleEditorView): RoleForm {
  return {
    name: role.name,
    permissions: new Set(role.permissions),
    restrictToOwnRecords: role.restrictToOwnRecords,
    requireMfa: role.requireMfa,
  }
}

export type GroupState = "all" | "none" | "some"

export function groupState(permissions: ReadonlySet<string>, group: readonly KnownPermission[]): GroupState {
  const held = group.filter((permission) => permissions.has(permission)).length
  if (held === 0) return "none"
  return held === group.length ? "all" : "some"
}

/** Нажатие по разделу: пустой и частичный становятся полными, полный — пустым. */
export function toggleGroup(permissions: ReadonlySet<string>, group: readonly KnownPermission[]): Set<string> {
  const next = new Set(permissions)
  if (groupState(permissions, group) === "all") {
    for (const permission of group) next.delete(permission)
  } else {
    for (const permission of group) next.add(permission)
  }
  return next
}

export function togglePermission(permissions: ReadonlySet<string>, permission: string): Set<string> {
  const next = new Set(permissions)
  if (next.has(permission)) next.delete(permission)
  else next.add(permission)
  return next
}

/** Есть ли несохранённые правки — «Сохранить роль» без правок не нужна. */
export function isDirty(role: CabinetRoleEditorView, form: RoleForm): boolean {
  if (form.name.trim() !== role.name) return true
  if (form.restrictToOwnRecords !== role.restrictToOwnRecords || form.requireMfa !== role.requireMfa) return true
  if (form.permissions.size !== role.permissions.length) return true
  return role.permissions.some((permission) => !form.permissions.has(permission))
}

/** Подпись под именем роли: чья она и правил ли её салон. */
export function roleOrigin(role: CabinetRoleEditorView): string {
  if (!role.isSystem) return "Своя роль салона"
  const template = SYSTEM_ROLES.find((item) => item.code === role.code)
  const base = template?.name ?? role.name
  if (template !== undefined && template.name !== role.name) {
    return `Системная роль «${base}», переименована салоном`
  }
  return role.modified ? `Системная роль «${base}», права изменены салоном` : `Системная роль «${base}», без правок`
}

/** «2 человека», «1 человек», «5 человек» — для подсказки об удалении. */
export function peopleWords(count: number): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} человека`
  return `${count} человек`
}

/** Почему роль нельзя удалить; null — можно. */
export function deleteDenial(role: CabinetRoleEditorView): string | null {
  if (role.isSystem) return "Системную роль удалить нельзя — только переименовать"
  if (role.membersCount > 0) {
    return `На роли «${role.name}» ${peopleWords(role.membersCount)} — сначала переведите их на другую роль`
  }
  return null
}

/** Разделы с их состоянием — для отрисовки и теста. */
export function sectionsOf(permissions: ReadonlySet<string>): readonly {
  readonly id: string
  readonly title: string
  readonly permissions: readonly KnownPermission[]
  readonly state: GroupState
  readonly held: number
}[] {
  return PERMISSION_GROUPS.map((group) => ({
    ...group,
    state: groupState(permissions, group.permissions),
    held: group.permissions.filter((permission) => permissions.has(permission)).length,
  }))
}
